import * as React from 'react';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import { ISuggestion } from '../../../../../../../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import EDKeybind from '../../../../../../../../../../../../BaseComponents/Keybinds/EDKeybind';
import EkonomiskAnalysUrvalSelectionWrappedAutocomplete from './EkonomiskAnalysUrvalSelectionWrappedAutocomplete';
import EDAutoCompleteInterval from '../../../../../../../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteInterval';

interface IProps {
  label?: string;
  onChange: (newValue: string, callback?: () => void) => void;
  codeLength?: number;
  onBlur?: () => void;
  value?: string;
  placeholder?: string;
  suggestions: ISuggestion[];
  utanforKodbehorighet: string[];
  onUpdate: () => void;
  autoFocus?: boolean;
  isEnumCondition: boolean;
  classes?: any;
  maxResult?: number;
}

interface IState {
  hideWarning: boolean;
}

const styles = (theme: Theme) => ({
  container: {
    display: 'flex',
    flex: '0 1 auto',
    flexDirection: 'column',
  },
  warningText: {
    alignSelf: 'flex-end',
  },
  helperTextContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  suggestionContainer: {
    display: 'flex',
    flex: '0 1 auto',
    whiteSpace: 'normal',
  },
  suggestionMenuItem: {
    height: '100%',
  },
  suggestionsContainerPopper: {
    zIndex: 1400,
    maxHeight: '300px',
    overflowY: 'auto',
    left: '17px !important',
    boxShadow: theme.shadows[8],
    borderRadius: theme.spacing(0.5),
  },
  popperPaper: {
    backgroundColor: theme.palette.grey['100'],
  },
  menuItemText: {
    wordBreak: 'break-word',
  },
});

class EkonomiskAnalysUrvalSelection extends React.PureComponent<
  IProps,
  IState
> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      hideWarning: true,
    };
  }

  getActiveWarning = (
    inputValue: string,
    suggestions: ISuggestion[]
  ): string => {
    const inputGroups: string[] = inputValue.split(/,/g);
    const group =
      inputGroups.length === 1
        ? inputGroups[0]
        : inputGroups[inputGroups.length - 1];

    const inputs: string[] = group.split(/-/g);
    const code = inputs.length === 1 ? inputs[0] : inputs[1];
    if (code === '') {
      return '';
    }
    if (Number.isNaN(+code)) {
      return !suggestions.find(suggestion =>
        suggestion.name.trim().toLowerCase().includes(code.trim().toLowerCase())
      )
        ? `Ingen träff innehåller ${code}`
        : '';
    } else {
      return suggestions.find(suggestion => {
        const suggestionCode = this.props.isEnumCondition
          ? suggestion.data?.code
          : suggestion.value;

        return (
          suggestionCode &&
          suggestionCode
            .trim()
            .toLowerCase()
            .startsWith(code.trim().toLowerCase())
        );
      }) === undefined
        ? `Ingen exakt träff för ${code}`
        : '';
    }
  };

  getSuggestionValue = (suggestion: ISuggestion | null): string => {
    if (!suggestion) {
      return '';
    }

    return this.props.isEnumCondition
      ? suggestion.data && suggestion.data.code
        ? suggestion.data.code
        : suggestion.name
      : suggestion.value;
  };

  getInputTooltip = (values: string[]) => {
    if (values.length === 0) {
      return '';
    }

    return `Utanför kodbehörighet eller grundurval: ${values.join(', ')}`;
  };

  renderAutocomplete =
    (warning?: string, typingWarning?: string) =>
    (autoComplete: React.ReactNode, fullscreen?: boolean) => {
      return (
        <EkonomiskAnalysUrvalSelectionWrappedAutocomplete
          autoComplete={autoComplete}
          fullscreen={fullscreen}
          warning={warning}
          typingWarning={typingWarning}
        />
      );
    };

  getOptionMatches(inputValue: string) {
    return (option: ISuggestion) => {
      const multipleValues = inputValue?.split(',');
      const latestInterval =
        multipleValues?.[multipleValues?.length - 1]?.split('-');
      const value = latestInterval?.[latestInterval?.length - 1];
      const label = option.name;
      const startIndex = label.toLowerCase().indexOf(value);
      return {
        option: option,
        startIndex: startIndex,
      };
    };
  }

  onBlur = () => {
    this.setState({ hideWarning: true });
    this.props.onBlur?.();
  };

  onChange = (newValue: string) => {
    this.setState({ hideWarning: false });
    this.props.onChange(newValue);
  };

  render() {
    const warning = this.getInputTooltip(this.props.utanforKodbehorighet);
    const typingWarning =
      !this.state.hideWarning && this.props.suggestions.length > 0
        ? this.getActiveWarning(this.props.value || '', this.props.suggestions)
        : undefined;
    return (
      <EDKeybind keymap="a" alt onKeyDown={this.props.onUpdate}>
        <>
          <EDAutoCompleteInterval
            inputMargin="dense"
            valueSeparator=","
            intervalSeparator="-"
            label={this.props.label || ''}
            inputValue={this.props.value}
            options={this.props.suggestions}
            onInputChange={this.onChange}
            getOptionLabel={(option: ISuggestion) => option.name ?? option}
            getSuggestionValue={this.getSuggestionValue}
            autoFocus={this.props.autoFocus}
            onBlur={this.onBlur}
            placeholder={this.props.placeholder}
            renderAutocomplete={this.renderAutocomplete(warning, typingWarning)}
            getOptionMatches={this.getOptionMatches}
            disableOpenOnFocus
            fullWidth
            disableInterval={this.props.isEnumCondition}
            wordMaxLength={this.props.codeLength}
            maxResult={this.props.maxResult}
          />
        </>
      </EDKeybind>
    );
  }
}

export default withStyles(EkonomiskAnalysUrvalSelection, styles);
